import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from "@headlessui/react"
import {
  ArrowLeftEndOnRectangleIcon,
  Bars3Icon,
  EnvelopeIcon,
  HomeIcon,
  NewspaperIcon,
  XMarkIcon
} from "@heroicons/react/24/outline"
import { useInvites, useUser } from "api/ApiHooks"
import { classNames } from "utils/ClassNames"
import { UniqueGradient } from "./UniqueGradient"
import { useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { LoversLiveForeverLogo } from "./LoversLiveForeverLogo"
import { OwnTimeCapsuleList } from "./OwnTimeCapsuleList"
import { SharedTimeCapsuleList } from "./SharedTimeCapsuleList"

const navigation = [
  { name: "Home", href: "/dashboard", icon: HomeIcon },
  { name: "Latest & greatest", href: "/latest", icon: NewspaperIcon },
  { name: "Invites", href: "/invites", icon: EnvelopeIcon }
]

const NavigationItems = (props: { onLinkClick?: () => void }) => {
  const { onLinkClick } = props
  const invitesQuery = useInvites()
  const location = useLocation()

  return (
    <ul role="list" className="flex flex-1 flex-col gap-y-7">
      <li>
        <ul role="list" className="-mx-2 space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                onClick={() => (onLinkClick ? onLinkClick() : undefined)}
                className={classNames(
                  item.href === location.pathname
                    ? "bg-gray-50 text-primary-600"
                    : "text-gray-700 hover:bg-gray-50 hover:text-primary-600",
                  "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
                )}
              >
                <div className="relative">
                  <item.icon
                    aria-hidden="true"
                    className={classNames(
                      item.href === location.pathname
                        ? "text-primary-600"
                        : "text-gray-400 group-hover:text-primary-600",
                      "h-6 w-6 shrink-0"
                    )}
                  />
                  {item.name === "Invites" &&
                    invitesQuery.data?.invites &&
                    invitesQuery.data.invites.length > 0 && (
                      <span className="inline-flex items-center justify-center h-5 w-5 bg-red-500 rounded-full text-white text-xs font-medium absolute -top-1 -right-2">
                        {invitesQuery.data.invites.length > 99
                          ? "99+"
                          : invitesQuery.data.invites.length}
                      </span>
                    )}
                </div>

                {item.name}
              </Link>
            </li>
          ))}
          {/* logout needs to be a tag to remove cookies properly */}
          <li key={"logout"}>
            <a
              href={"/logout"}
              className={classNames(
                "text-gray-700 hover:bg-gray-50 hover:text-primary-600",
                "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold"
              )}
            >
              <ArrowLeftEndOnRectangleIcon
                aria-hidden="true"
                className={classNames(
                  "text-gray-400 group-hover:text-primary-600",
                  "h-6 w-6 shrink-0"
                )}
              />
              Logout
            </a>
          </li>
        </ul>
      </li>

      <li>
        <SharedTimeCapsuleList onClick={onLinkClick} />
      </li>
      <li>
        <OwnTimeCapsuleList onClick={onLinkClick} />
      </li>
    </ul>
  )
}

const UserProfile: React.FC = () => {
  const { data } = useUser()
  return (
    <Link
      to="/profile"
      className="flex items-center gap-x-4 lg:px-6 lg:py-3 text-sm/6 font-semibold text-gray-900 lg:hover:bg-gray-50"
    >
      {data?.user.profileImageUrl ? (
        <img
          src={data.user.profileImageUrl}
          alt=""
          className="h-8 w-8 rounded-full bg-gray-50"
        />
      ) : (
        <UniqueGradient id={data?.user.id} className="h-8 w-8 rounded-full bg-gray-50" />
      )}
      <span className="sr-only">Your profile</span>
      <span aria-hidden="true" className="hidden lg:block">
        {data?.user.name}
      </span>
    </Link>
  )
}

const MobileSidebar = () => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const { data } = useUser()

  return (
    <>
      <Dialog
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        className="relative z-50 lg:hidden"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
        />

        <div className="fixed inset-0 flex">
          <DialogPanel
            transition
            className="relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
          >
            <TransitionChild>
              <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="-m-2.5 p-2.5"
                >
                  <span className="sr-only">Close sidebar</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6 text-white" />
                </button>
              </div>
            </TransitionChild>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-2">
              <div className="mt-4 items-center">
                <LoversLiveForeverLogo className="h-14 w-auto" />
              </div>
              <nav className="flex flex-1 flex-col">
                <NavigationItems onLinkClick={() => setSidebarOpen(false)} />
              </nav>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      <div className="sticky top-0 z-40 flex items-center justify-between gap-x-6 bg-white px-4 py-4 shadow-sm sm:px-6 lg:hidden">
        <button
          type="button"
          onClick={() => setSidebarOpen(true)}
          className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
        >
          <span className="sr-only">Open sidebar</span>
          <Bars3Icon aria-hidden="true" className="h-6 w-6" />
        </button>
        <div>
          <LoversLiveForeverLogo className="h-14 w-auto" />
        </div>
        <div className="text-right">
          <span className="sr-only">Your profile</span>
          <Link to="/profile">
            <p className="text-xs font-semibold text-gray-500">hello</p>
            <p className="text-sm font-semibold text-gray-900">{data?.user.name}</p>
          </Link>
        </div>
      </div>
    </>
  )
}

const DesktopSidebar = () => {
  return (
    <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
      <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6">
        <div className="items-center mt-4">
          <LoversLiveForeverLogo className="h-14 w-auto" />
        </div>
        <nav className="flex flex-1 flex-col">
          <ul role="list" className="flex flex-1 flex-col gap-y-7">
            <NavigationItems />
            <li className="-mx-6 mt-auto">
              <UserProfile />
            </li>
          </ul>
        </nav>
      </div>
    </div>
  )
}

export const Sidebar = () => {
  return (
    <>
      <MobileSidebar />
      <DesktopSidebar />
    </>
  )
}
